import { Eye, EyeOff } from 'lucide-react';
import { useId, useState, type SyntheticEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';

import {
  AuthField as Field,
  authInputClass as inputClass,
  AuthLayout,
  AuthNotice,
  AuthSubmit,
} from '../features/auth/AuthLayout';
import { useAuth } from '../features/auth/AuthProvider';
import { loginErrorMessage } from '../features/auth/loginError';
import { safeNext } from '../features/auth/redirect';
import { readTenant, rememberTenant } from '../features/auth/tenantMemory';

export function LoginPage() {
  const { status, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(params.get('next'));

  const [tenant, setTenant] = useState(readTenant);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();

  if (status === 'authenticated' && !submitting) return <Navigate to={next} replace />;

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login({ tenant: tenant.trim(), email: email.trim(), password });
      rememberTenant(tenant.trim().toLowerCase());
      void navigate(next, { replace: true });
    } catch (err) {
      setError(loginErrorMessage(err));
      setPassword('');
      setSubmitting(false);
    }
  };

  const describedBy = error ? errorId : undefined;

  return (
    <AuthLayout title="Entrar" subtitle="Use as credenciais da sua organização.">
      <form
        className="mt-5 space-y-4"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <Field label="Organização" htmlFor="tenant">
          <input
            id="tenant"
            name="tenant"
            autoComplete="organization"
            autoCapitalize="none"
            spellCheck={false}
            required
            autoFocus={tenant === ''}
            value={tenant}
            onChange={(e) => {
              setTenant(e.target.value);
            }}
            aria-describedby={describedBy}
            className={inputClass}
            placeholder="ex.: minha-empresa"
          />
        </Field>
        <Field label="E-mail" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            autoFocus={tenant !== ''}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            aria-describedby={describedBy}
            className={inputClass}
          />
        </Field>
        <Field label="Senha" htmlFor="password">
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              aria-describedby={describedBy}
              className={`${inputClass} pr-10`}
            />
            <button
              type="button"
              onClick={() => {
                setShowPassword((v) => !v);
              }}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 grid w-9 place-items-center rounded-r-md text-fg-subtle hover:text-fg"
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" className="size-4" strokeWidth={1.75} />
              ) : (
                <Eye aria-hidden="true" className="size-4" strokeWidth={1.75} />
              )}
            </button>
          </div>
        </Field>

        {error && (
          <AuthNotice id={errorId} tone="error">
            {error}
          </AuthNotice>
        )}

        <AuthSubmit
          busy={submitting}
          disabled={!tenant.trim() || !email.trim() || !password}
          label="Entrar"
          busyLabel="Entrando…"
        />
        <p className="text-center text-[13px]">
          <Link
            to={`/forgot-password?tenant=${encodeURIComponent(tenant.trim())}`}
            className="text-accent hover:underline"
          >
            Esqueci minha senha
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
